'use client';
import Link from 'next/link';
import Navbar from '@/components/ui/Navbar';
import Footer from '@/components/ui/Footer';

const SITE_NAME = 'RePrint Solution';
const SITE_URL = 'https://reprintsolution.online';
const CONTACT_EMAIL = 'info@reprintsolution.online';
const LAST_UPDATED = '1 June 2026';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#030812] text-white">
      <Navbar />
      <main>
        <section className="border-b border-white/10 bg-[linear-gradient(135deg,#07111f_0%,#0f1f3d_100%)] py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <nav className="mb-6 flex items-center gap-2 text-sm text-slate-400">
              <Link href="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-white">Terms &amp; Conditions</span>
            </nav>
            <h1 className="text-3xl font-black text-white md:text-4xl">Terms &amp; Conditions</h1>
            <p className="mt-3 text-slate-400 text-sm">Last updated: {LAST_UPDATED}</p>
          </div>
        </section>

        <section className="py-14">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="space-y-8 text-sm leading-7 text-slate-300">

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">1. Acceptance of Terms</h2>
                <p className="mt-3">
                  By accessing and using the {SITE_NAME} website at {SITE_URL} (the &quot;Site&quot;), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our Site.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">2. Use of the Site</h2>
                <p className="mt-3">You agree to use this Site only for lawful purposes and in a manner that does not infringe the rights of others. You must not:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li>Use the Site in any way that violates applicable local, national, or international laws</li>
                  <li>Transmit any unsolicited or unauthorised advertising or promotional material</li>
                  <li>Attempt to gain unauthorised access to any part of the Site</li>
                  <li>Engage in any conduct that restricts or inhibits anyone&apos;s use or enjoyment of the Site</li>
                </ul>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">3. Product Information</h2>
                <p className="mt-3">
                  {SITE_NAME} is an independent refurbished printer information and retail platform. We make every effort to ensure product descriptions, specifications, and condition grades are accurate. However, we do not warrant that product descriptions or other content on the Site is accurate, complete, reliable, current, or error-free.
                </p>
                <p className="mt-3">
                  All brand names, product names, logos, and trademarks mentioned on this Site are the property of their respective owners. {SITE_NAME} is not affiliated with, sponsored by, or endorsed by any printer manufacturer or brand.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">4. Pricing and Availability</h2>
                <p className="mt-3">
                  Prices displayed on the Site are subject to change without notice. Product availability is not guaranteed. We reserve the right to limit quantities, refuse orders, or cancel orders at our discretion.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">5. Intellectual Property</h2>
                <p className="mt-3">
                  The content on this Site, including but not limited to text, graphics, logos, images, and software, is the property of {SITE_NAME} or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">6. Disclaimer of Warranties</h2>
                <p className="mt-3">
                  This Site is provided on an &quot;as is&quot; and &quot;as available&quot; basis without any warranties of any kind, either express or implied. We do not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">7. Limitation of Liability</h2>
                <p className="mt-3">
                  To the fullest extent permitted by law, {SITE_NAME} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, the Site or its content.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">8. Third-Party Links</h2>
                <p className="mt-3">
                  Our Site may contain links to third-party websites. These links are provided for your convenience only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">9. Advertising</h2>
                <p className="mt-3">
                  This Site may display advertisements served by Google AdSense and other advertising networks. These advertisements are clearly identified as ads. We are not responsible for the content of third-party advertisements. Clicking on advertisements may direct you to third-party websites governed by their own terms and privacy policies.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">10. Changes to Terms</h2>
                <p className="mt-3">
                  We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site after any changes constitutes your acceptance of the new terms.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">11. Governing Law</h2>
                <p className="mt-3">
                  These Terms and Conditions shall be governed by and construed in accordance with applicable laws. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the relevant courts.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">12. Contact</h2>
                <p className="mt-3">
                  For questions about these Terms and Conditions, please contact us at:{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-400 hover:text-violet-300 underline">{CONTACT_EMAIL}</a>
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
